import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Megaphone,
  LineChart,
  Package,
  KanbanSquare,
  FileText,
  Users,
  Sparkles,
  Shield,
  UserCog,
  LogOut,
  Workflow,
  CreditCard,
  Flame,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const main = [
  { title: "Executive Briefing", url: "/dashboard", icon: LayoutDashboard },
  { title: "Projects Board", url: "/projects", icon: KanbanSquare },
  { title: "CRM", url: "/crm", icon: Users },
  { title: "AI Assistant", url: "/assistant", icon: Sparkles },
  { title: "Automations", url: "/automations", icon: Workflow },
  { title: "Reports", url: "/reports", icon: FileText },
  { title: "Plans & billing", url: "/pricing", icon: CreditCard },
  { title: "DIVINEFLOW", url: "/divineflow", icon: Flame },
];

const departments = [
  { title: "Project Director", url: "/dept/project-director", icon: Briefcase },
  { title: "Marketing & Brand", url: "/dept/marketing", icon: Megaphone },
  { title: "Trading Systems", url: "/dept/trading", icon: LineChart },
  { title: "Revenue Architect", url: "/dept/revenue", icon: Package },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  const isActive = (p: string) => pathname === p || pathname.startsWith(p + "/");
  const linkClass = (active: boolean) =>
    active
      ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
      : "hover:bg-sidebar-accent/50";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-gradient-hero shadow-pop flex items-center justify-center text-white font-bold text-sm">
            E
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold">Enterprise Suite</span>
              <span className="text-[10px] text-muted-foreground">Business Operations</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Workspace</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {main.map((i) => (
                <SidebarMenuItem key={i.url}>
                  <SidebarMenuButton asChild isActive={isActive(i.url)}>
                    <NavLink to={i.url} className={linkClass(isActive(i.url))}>
                      <i.icon className="h-4 w-4" />
                      {!collapsed && <span>{i.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Departments</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {departments.map((i) => (
                <SidebarMenuItem key={i.url}>
                  <SidebarMenuButton asChild isActive={isActive(i.url)}>
                    <NavLink to={i.url} className={linkClass(isActive(i.url))}>
                      <i.icon className="h-4 w-4" />
                      {!collapsed && <span>{i.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {user?.role === "admin" && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive("/admin")}>
                    <NavLink to="/admin" className={linkClass(isActive("/admin"))}>
                      <Shield className="h-4 w-4" />
                      {!collapsed && <span>Admin Panel</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        {!collapsed && user && (
          <div className="px-2 pb-2 text-xs">
            <div className="font-medium truncate">{user.name}</div>
            <div className="text-muted-foreground truncate">{user.email}</div>
          </div>
        )}
        <SidebarMenuButton asChild isActive={isActive("/profile")}>
          <NavLink to="/profile" className={linkClass(isActive("/profile"))}>
            <UserCog className="h-4 w-4" />
            {!collapsed && <span>My Profile</span>}
          </NavLink>
        </SidebarMenuButton>
        <Button variant="ghost" size="sm" onClick={logout} className="w-full justify-start gap-2">
          <LogOut className="h-4 w-4" />
          {!collapsed && <span>Sign out</span>}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
